import { commonComponents as cc } from "@/components/common";
import { JourneyProgressSummary, type JourneyProgressStep } from "@/components/pages/SuccessPage";
import { formComponents as fc } from "@/features/i18n/formComponents";
import PreferencesForm from "@/features/knock/components/PreferencesForm";
import FormPage from "@/features/pages/components/FormPage";
import { useNav } from "@/utils/nav";
import { FileInput } from "@patient-watch/forms";
import type { FormViewT, InputT } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import { createFileRoute, type AnyRoute } from "@tanstack/react-router";
import {
    Activity,
    CalendarDays,
    Check,
    ClipboardSignature,
    FileText,
    HeartPulse,
    Mail,
    Phone,
    ShieldCheck,
    UploadCloud,
    UserRound,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";

export const Route = createFileRoute("/_app/design/features/otivio-frictionless-onboarding")({
    component: OtivioFrictionlessOnboardingDemo,
});

type DemoStage = "Profile" | "Consent" | "Set-up" | "Forms";
type DemoPageKind = "text" | "date" | "email" | "phone" | "choice" | "consent" | "upload" | "form" | "success";
type DemoStateGroup = "onboarding" | "consent" | "setup" | "forms";
type DemoStateStatus = "Todo" | "Current" | "Done";

interface DemoFormValues {
    name: string;
    dob_day: number;
    dob_month: number;
    dob_year: number;
    email: string;
    phone: string;
    preference: string;
    pms_consent: boolean;
    reimbursement_consent: boolean;
    hospital_number: string;
    treatment_start: string | null;
    prescription: File | null;
    ms_type: string;
    nrs_score: string;
    msis_response: string;
}

interface DemoPage {
    id: string;
    stage: DemoStage;
    title: string;
    description?: string;
    label: string;
    value?: string;
    kind: DemoPageKind;
    icon: ReactNode;
    options?: string[];
    nextLabel?: string;
}

interface DemoStateItem {
    name: string;
    status: DemoStateStatus;
}

type DemoState = Record<DemoStateGroup, DemoStateItem[]>;

const DEMO_PAGES: DemoPage[] = [
    {
        id: "name",
        stage: "Profile",
        title: "What is your name?",
        description: "This is how your clinician will identify you in Patient Watch.",
        label: "Name",
        value: "Anna Jensen",
        kind: "text",
        icon: <UserRound className="w-5 h-5" />,
    },
    {
        id: "dob",
        stage: "Profile",
        title: "What is your date of birth?",
        description: "We use this to match your FlowOx record safely.",
        label: "DOB",
        value: "14 March 1978",
        kind: "date",
        icon: <CalendarDays className="w-5 h-5" />,
    },
    {
        id: "email",
        stage: "Profile",
        title: "What is your email?",
        description: "We will only use this for your Patient Watch account and programme messages.",
        label: "Email",
        value: "anna.jensen@example.dk",
        kind: "email",
        icon: <Mail className="w-5 h-5" />,
    },
    {
        id: "phone",
        stage: "Profile",
        title: "What is your phone number?",
        description: "SMS reminders help you complete forms at the right time.",
        label: "Phone",
        value: "+45 24 12 34 56",
        kind: "phone",
        icon: <Phone className="w-5 h-5" />,
    },
    {
        id: "preference",
        stage: "Profile",
        title: "How should we remind you?",
        description: "Choose the reminder channel that feels easiest.",
        label: "Preference",
        value: "SMS",
        kind: "choice",
        icon: <Mail className="w-5 h-5" />,
        options: ["SMS", "Email", "Both"],
    },
    {
        id: "profile-success",
        stage: "Profile",
        title: "Profile complete",
        description: "Your profile details have been saved. Next we will review consent.",
        label: "Profile saved",
        kind: "success",
        icon: <UserRound className="w-5 h-5" />,
        nextLabel: "Continue to consent",
    },
    {
        id: "pms-consent",
        stage: "Consent",
        title: "Post-market surveillance",
        description: "Please review and confirm this consent before continuing.",
        label: "Consent to data collection for post-market surveillance",
        kind: "consent",
        icon: <ShieldCheck className="w-5 h-5" />,
    },
    {
        id: "reimbursement-consent",
        stage: "Consent",
        title: "Reimbursement consent",
        description: "This allows relevant programme information to support reimbursement documentation.",
        label: "Consent for reimbursement",
        kind: "consent",
        icon: <ClipboardSignature className="w-5 h-5" />,
    },
    {
        id: "consent-success",
        stage: "Consent",
        title: "Consent complete",
        description: "Your consent choices have been recorded. Now we just need your treatment set-up details.",
        label: "Consent signed",
        kind: "success",
        icon: <ShieldCheck className="w-5 h-5" />,
        nextLabel: "Continue to set-up",
    },
    {
        id: "hospital-number",
        stage: "Set-up",
        title: "What is your hospital number?",
        description: "You can skip this if you do not know it.",
        label: "Hospital number",
        value: "DK-2048-1192",
        kind: "text",
        icon: <FileText className="w-5 h-5" />,
    },
    {
        id: "treatment-start",
        stage: "Set-up",
        title: "When does treatment start?",
        description: "Your reminders and baseline forms will use this date.",
        label: "Date of treatment starting",
        value: "22 July 2026",
        kind: "date",
        icon: <CalendarDays className="w-5 h-5" />,
    },
    {
        id: "setup-success",
        stage: "Set-up",
        title: "Set-up complete",
        description: "Your treatment details are ready. Next you can complete the first FlowOx forms.",
        label: "Set-up saved",
        kind: "success",
        icon: <CalendarDays className="w-5 h-5" />,
        nextLabel: "Start forms",
    },
    {
        id: "prescription-upload",
        stage: "Forms",
        title: "Prescription Upload",
        description: "Upload the prescription documentation needed for the FlowOx programme.",
        label: "Prescription document",
        value: "flowox-prescription.pdf",
        kind: "upload",
        icon: <UploadCloud className="w-5 h-5" />,
    },
    {
        id: "prescription-upload-success",
        stage: "Forms",
        title: "Prescription uploaded",
        description: "Your prescription document has been added to the FlowOx programme record.",
        label: "Prescription Upload complete",
        kind: "success",
        icon: <UploadCloud className="w-5 h-5" />,
        nextLabel: "Continue to MS Profile",
    },
    {
        id: "ms-profile",
        stage: "Forms",
        title: "MS Profile",
        description: "One question at a time, following the same form page pattern patients already use.",
        label: "What type of MS have you been diagnosed with?",
        value: "Relapsing-remitting MS",
        kind: "choice",
        icon: <HeartPulse className="w-5 h-5" />,
        options: ["Relapsing-remitting MS", "Primary progressive MS", "Secondary progressive MS", "Not sure"],
    },
    {
        id: "ms-profile-success",
        stage: "Forms",
        title: "MS Profile complete",
        description: "Your MS profile response has been saved.",
        label: "MS Profile complete",
        kind: "success",
        icon: <HeartPulse className="w-5 h-5" />,
        nextLabel: "Continue to NRS",
    },
    {
        id: "nrs",
        stage: "Forms",
        title: "NRS",
        description: "A simple numeric rating page with a clear explicit next action.",
        label: "How would you rate your symptoms today?",
        value: "4",
        kind: "form",
        icon: <Activity className="w-5 h-5" />,
        options: ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
    },
    {
        id: "nrs-success",
        stage: "Forms",
        title: "NRS complete",
        description: "Your symptom rating has been saved.",
        label: "NRS complete",
        kind: "success",
        icon: <Activity className="w-5 h-5" />,
        nextLabel: "Continue to MSIS-29",
    },
    {
        id: "msis-29",
        stage: "Forms",
        title: "MSIS-29",
        description: "Validated form content should remain one item per screen with progress visible.",
        label: "In the past two weeks, how much has MS limited your ability to do physically demanding tasks?",
        value: "Moderately",
        kind: "form",
        icon: <FileText className="w-5 h-5" />,
        options: ["Not at all", "A little", "Moderately", "Quite a bit", "Extremely"],
    },
    {
        id: "msis-29-success",
        stage: "Forms",
        title: "All done",
        description: "Your MSIS-29 response has been saved. The first Otivio onboarding tasks are complete.",
        label: "MSIS-29 complete",
        kind: "success",
        icon: <FileText className="w-5 h-5" />,
        nextLabel: "Finish demo",
    },
];

const DEMO_DEFAULT_VALUES: DemoFormValues = {
    name: "Anna Jensen",
    dob_day: 14,
    dob_month: 3,
    dob_year: 1978,
    email: "anna.jensen@example.dk",
    phone: "+4524123456",
    preference: "SMS",
    pms_consent: true,
    reimbursement_consent: true,
    hospital_number: "DK-2048-1192",
    treatment_start: "2026-07-22T09:00:00.000Z",
    prescription: null,
    ms_type: "Relapsing-remitting MS",
    nrs_score: "4",
    msis_response: "Moderately",
};

const STAGES: DemoStage[] = ["Profile", "Consent", "Set-up", "Forms"];

const STATE_GROUP_BY_STAGE: Record<DemoStage, DemoStateGroup> = {
    Profile: "onboarding",
    Consent: "consent",
    "Set-up": "setup",
    Forms: "forms",
};

function getRespondentPages(stage: DemoStage) {
    return DEMO_PAGES.filter((page) => page.stage === stage && page.kind !== "success");
}

function buildJourneySteps(currentIndex: number): JourneyProgressStep[] {
    const currentPage = DEMO_PAGES[currentIndex] ?? DEMO_PAGES[0];
    const currentStage = currentPage.stage;
    const activeIndex = STAGES.indexOf(currentStage);

    return STAGES.map((stage, index) => {
        const stagePages = getRespondentPages(stage);
        const completedPages = stagePages.filter((page) => DEMO_PAGES.indexOf(page) < currentIndex).length;
        const value =
            index < activeIndex
                ? 100
                : index === activeIndex && stagePages.length > 0
                  ? Math.round((completedPages / stagePages.length) * 100)
                  : 0;

        return {
            label: stage,
            state: index === activeIndex ? "current" : index < activeIndex ? "complete" : "upcoming",
            value,
        };
    });
}

function demoInput(overrides: Partial<InputT> & Pick<InputT, "input_id" | "input_key" | "input_label">): InputT {
    return {
        input_id: overrides.input_id,
        input_key: overrides.input_key,
        input_label: overrides.input_label,
        input_description: overrides.input_description ?? null,
        input_caption: overrides.input_caption ?? null,
        is_optional: overrides.is_optional ?? false,
        scale_options: overrides.scale_options ?? [],
        options: overrides.options ?? [],
        min_value: overrides.min_value ?? null,
        max_value: overrides.max_value ?? null,
        unit: overrides.unit ?? null,
        decimal_points: overrides.decimal_points ?? null,
        input_list: overrides.input_list ?? null,
        input_index: overrides.input_index ?? 0,
        ...overrides,
    } as InputT;
}

function demoFormForPage(page: DemoPage): FormViewT {
    const input =
        page.id === "ms-profile"
            ? demoInput({
                  input_id: "demo_ms_type",
                  input_key: "single-choice",
                  input_label: page.label,
                  options: page.options ?? [],
              })
            : page.id === "nrs"
              ? demoInput({
                    input_id: "demo_nrs_score",
                    input_key: "single-choice",
                    input_label: page.label,
                    scale_options: (page.options ?? []).map((option) => ({
                        label: option,
                        value: Number(option),
                    })),
                })
              : demoInput({
                    input_id: "demo_msis_response",
                    input_key: "single-choice",
                    input_caption: "In the past two weeks",
                    input_label: page.label,
                    scale_options: (page.options ?? []).map((option, index) => ({
                        label: option,
                        value: index + 1,
                    })),
                });

    return {
        form_id: `00000000-0000-4000-a000-${page.id === "ms-profile" ? "000000000101" : page.id === "nrs" ? "000000000102" : "000000000103"}`,
        form_name: page.title,
        form_caption: page.title,
        form_description: page.description ?? null,
        form_instructions: page.id === "msis-29" ? "Please select one response." : null,
        copyright_info: null,
        canonical_form_id: null,
        locale: null,
        chart_type: null,
        pages: [
            {
                page_id: `demo-page-${page.id}`,
                page_index: 1,
                page_label: page.title,
                page_description: page.description ?? null,
                inputs: [input],
            },
        ],
    } as unknown as FormViewT;
}

function getStateName(page: DemoPage) {
    if (page.id === "pms-consent") return "Post-market surveillance";
    if (page.id === "reimbursement-consent") return "Reimbursement";
    if (page.id === "prescription-upload") return "Prescription Upload";
    if (page.id === "ms-profile") return "MS Profile";
    if (page.id === "nrs") return "NRS";
    if (page.id === "msis-29") return "MSIS-29";
    return page.label;
}

function buildDemoState(currentIndex: number): DemoState {
    return DEMO_PAGES.reduce<DemoState>(
        (state, page, index) => {
            if (page.kind === "success") return state;

            const group = STATE_GROUP_BY_STAGE[page.stage];
            const status: DemoStateStatus = index < currentIndex ? "Done" : index === currentIndex ? "Current" : "Todo";

            state[group].push({
                name: getStateName(page),
                status,
            });

            return state;
        },
        {
            onboarding: [],
            consent: [],
            setup: [],
            forms: [],
        }
    );
}

function DemoStatePanel({ state }: { state: DemoState }) {
    return (
        <ui.Card className="bg-slate-950 text-white">
            <ui.CardHeader>
                <ui.CardTitle className="text-white normal-case">Live demo state</ui.CardTitle>
                <ui.CardDescription className="text-slate-300">
                    This is the simplified state shape driving the progress UI.
                </ui.CardDescription>
            </ui.CardHeader>
            <ui.CardContent>
                <pre className="bg-black/30 p-4 rounded-xl overflow-x-auto text-indigo-100 text-xs leading-relaxed">
                    {JSON.stringify(state, null, 2)}
                </pre>
            </ui.CardContent>
        </ui.Card>
    );
}

function DateOfBirthDemoField() {
    return (
        <div className="flex flex-col gap-y-3">
            <ui.Label>Date of birth</ui.Label>
            <div className="flex gap-x-2">
                <div className="flex flex-[1_1_0]">
                    <fc.Input name="dob_day" type="number" placeholder="DD" />
                </div>
                <div className="flex flex-[1_1_0]">
                    <fc.Input name="dob_month" type="number" placeholder="MM" />
                </div>
                <div className="flex flex-[2_1_0]">
                    <fc.Input name="dob_year" type="number" placeholder="YYYY" />
                </div>
            </div>
            <p className="text-slate-500 text-sm">For example, 14 03 1978.</p>
        </div>
    );
}

function ConsentField({ page, name }: { page: DemoPage; name: "pms_consent" | "reimbursement_consent" }) {
    return (
        <div className="space-y-6">
            <ui.Card className="bg-white shadow-none border-slate-200">
                <ui.CardHeader>
                    <ui.CardTitle className="text-xl normal-case">{page.label}</ui.CardTitle>
                    <ui.CardDescription>
                        I confirm that I have read and understood this consent statement.
                    </ui.CardDescription>
                </ui.CardHeader>
                <ui.CardContent>
                    <ui.FormField
                        name={name}
                        render={({ field }) => (
                            <ui.FormItem className="flex flex-row items-start space-x-3 space-y-0 bg-slate-50 p-4 border rounded-md w-full">
                                <ui.FormControl>
                                    <ui.Checkbox checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                                </ui.FormControl>
                                <ui.FormLabel className="font-normal text-muted-foreground text-sm leading-relaxed">
                                    I agree to the terms outlined above and understand that my signature will be
                                    recorded.
                                </ui.FormLabel>
                            </ui.FormItem>
                        )}
                    />
                </ui.CardContent>
            </ui.Card>
        </div>
    );
}

function SuccessField({ page }: { page: DemoPage }) {
    return (
        <div className="flex flex-col items-center gap-5 py-6 text-center">
            <span className="flex justify-center items-center bg-indigo-600 shadow-sm rounded-full w-20 h-20 text-white">
                <Check className="w-10 h-10" />
            </span>
            <div className="space-y-2">
                <p className="font-semibold text-slate-950 text-xl">{page.label}</p>
                <p className="text-muted-foreground text-sm leading-relaxed">{page.description}</p>
            </div>
        </div>
    );
}

function DemoPageField({
    page,
    demoForm,
    onBack,
    onNext,
}: {
    page: DemoPage;
    demoForm: UseFormReturn<Record<string, unknown>>;
    onBack: () => void;
    onNext: () => void;
}) {
    if (page.kind === "success") return <SuccessField page={page} />;
    if (page.id === "name") return <fc.Input name="name" label="Name" placeholder="e.g. Anna Jensen" autoFocus />;
    if (page.id === "dob") return <DateOfBirthDemoField />;
    if (page.id === "email") return <fc.Email name="email" label="Email" />;
    if (page.id === "phone") return <fc.Phone name="phone" label="Phone" locale="da-DK" defaultCountry="DK" />;
    if (page.id === "preference") {
        return <PreferencesForm isDialog={false} isDemo demoDefaults={{ email: true, sms: true }} onSuccess={onNext} />;
    }
    if (page.id === "pms-consent") return <ConsentField page={page} name="pms_consent" />;
    if (page.id === "reimbursement-consent") return <ConsentField page={page} name="reimbursement_consent" />;
    if (page.id === "hospital-number") {
        return <fc.Input name="hospital_number" label="Hospital number" placeholder="Hospital number" />;
    }
    if (page.id === "treatment-start") {
        return (
            <fc.DateTime
                name="treatment_start"
                label="Date of treatment starting"
                required
                datePlaceholder="Pick a date"
                clearLabel="Clear date and time"
            />
        );
    }
    if (page.id === "prescription-upload") {
        return (
            <FileInput
                name="prescription"
                label="Prescription document"
                description="Upload a PDF, image, or document from your device."
            />
        );
    }
    if (page.id === "ms-profile") {
        return (
            <FormPage
                route={Route as unknown as AnyRoute}
                pageIndex={1}
                form={demoFormForPage(page)}
                formType="tracker"
                reactHookForm={demoForm}
                onSubmit={() => onNext()}
                prevPage={onBack}
                isLastPage
                diaryId="demo-diary"
            />
        );
    }
    if (page.kind === "form") {
        return (
            <FormPage
                route={Route as unknown as AnyRoute}
                pageIndex={1}
                form={demoFormForPage(page)}
                formType="tracker"
                reactHookForm={demoForm}
                onSubmit={() => onNext()}
                prevPage={onBack}
                isLastPage
                diaryId="demo-diary"
            />
        );
    }

    return null;
}

function PatientPageShell({
    page,
    pageIndex,
    form,
    demoForm,
    onBack,
    onNext,
}: {
    page: DemoPage;
    pageIndex: number;
    form: UseFormReturn<DemoFormValues>;
    demoForm: UseFormReturn<Record<string, unknown>>;
    onBack: () => void;
    onNext: () => void;
}) {
    const isFirst = pageIndex === 0;
    const isLast = pageIndex === DEMO_PAGES.length - 1;
    const primaryAction = page.kind === "success" ? "next" : page.stage === "Consent" ? "submit" : "next";
    const primaryLabel = page.nextLabel ?? (page.stage === "Consent" ? "Sign Consent" : isLast ? "Submit" : "Next");
    const pageOwnsForm = page.id === "preference" || page.kind === "form";
    const showJourneyProgress = page.kind === "success" || page.stage !== "Forms";

    return (
        <section className="mx-auto w-full max-w-md">
            <div className="flex flex-col gap-y-6 w-full">
                {showJourneyProgress ? (
                    <JourneyProgressSummary currentStageLabel={page.stage} steps={buildJourneySteps(pageIndex)} />
                ) : null}
                <cc.Title words={page.title} description={page.description} headingVariant="compact" />

                {pageOwnsForm ? (
                    <DemoPageField page={page} demoForm={demoForm} onBack={onBack} onNext={onNext} />
                ) : (
                    <ui.Form {...form}>
                        <form className="space-y-6" onSubmit={(event) => event.preventDefault()}>
                            <DemoPageField page={page} demoForm={demoForm} onBack={onBack} onNext={onNext} />
                            <cc.FooterButtons
                                func1={onNext}
                                func2={isFirst ? undefined : onBack}
                                words1={primaryLabel}
                                words2={isFirst ? undefined : "Back"}
                                primaryAction={primaryAction}
                                secondaryAction="back"
                                type1="button"
                            />
                        </form>
                    </ui.Form>
                )}
            </div>
        </section>
    );
}

function OtivioFrictionlessOnboardingDemo() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        { label: "Otivio onboarding", route: { to: "/design/features/otivio-frictionless-onboarding" } },
    ]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const form = useForm<DemoFormValues>({
        defaultValues: DEMO_DEFAULT_VALUES,
    });
    const demoForm = useForm<Record<string, unknown>>({
        defaultValues: {
            demo_ms_type: "Relapsing-remitting MS",
            demo_nrs_score: "4",
            demo_msis_response: "3",
        },
    });
    const currentPage = DEMO_PAGES[currentIndex] ?? DEMO_PAGES[0];
    const demoState = buildDemoState(currentIndex);

    const goBack = () => setCurrentIndex((index) => Math.max(0, index - 1));
    const goNext = () => setCurrentIndex((index) => (index >= DEMO_PAGES.length - 1 ? 0 : index + 1));

    return (
        <section className="bg-gradient-to-br from-indigo-50 via-slate-50 to-white min-h-screen">
            <div className="gap-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_520px] mx-auto px-4 sm:px-8 py-8 max-w-6xl">
                <div className="flex flex-col justify-center space-y-6">
                    <div className="space-y-4">
                        <ui.Badge className="bg-indigo-100 hover:bg-indigo-100 text-indigo-700">
                            Developer demo - Otivio
                        </ui.Badge>
                        <div className="space-y-3 max-w-3xl">
                            <h1 className="font-bold text-slate-950 text-4xl lg:text-5xl tracking-tight">
                                Otivio onboarding
                            </h1>
                            <p className="text-slate-600 text-lg leading-relaxed">
                                Preview a simple frictionless patient flow for Profile, Consent, Set-up, and Forms.
                            </p>
                        </div>
                    </div>
                    <DemoStatePanel state={demoState} />
                </div>

                <div className="lg:top-8 lg:sticky h-fit">
                    <div className="bg-white/70 shadow-xl backdrop-blur mx-auto p-5 border border-white rounded-[2rem] w-full max-w-[430px] aspect-[9/16] overflow-y-auto">
                        <PatientPageShell
                            page={currentPage}
                            pageIndex={currentIndex}
                            form={form}
                            demoForm={demoForm}
                            onBack={goBack}
                            onNext={goNext}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
}
